import { Component, OnInit, Inject } from '@angular/core';
import { MatAutocompleteSelectedEvent } from '@angular/material/autocomplete';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { UntypedFormGroup, UntypedFormControl, UntypedFormBuilder, Validators } from '@angular/forms';
import { CreateUpdateProfileFormError, ShareReport, AcknowledgementDialog } from 'src/app/shared/error-messages';
import { Observable, of } from 'rxjs';
import { Constants } from 'src/app/shared/constants';
import { OrderResult, ReportDTO } from 'src/app/core/classes/orderResult';
import { ResultsService } from '../Service/results.service';
import { EmailFaxShare } from 'src/app/core/classes/email-fax-response';
import { startWith, map } from 'rxjs/operators';
import { EmailFaxRequest } from 'src/app/core/classes/email-fax-request';
import { NotificationService } from 'src/app/shared/services/notification-service';
import { CreateCustomerUserService } from '../../manage-customer-users/Service/create-customer-user.service';
import { LoaderService } from 'src/app/shared/loader/loader.service';

@Component({
    selector: 'app-share-report-dialog',
    templateUrl: './share-report-dialog.component.html',
    styleUrls: ['./share-report-dialog.component.scss'],
    standalone: false
})
export class ShareReportDialogComponent implements OnInit {
  public DialogText = 'Share Report';
  shareControl = new UntypedFormGroup({
    emails: new UntypedFormControl(),
    faxes: new UntypedFormControl()
  });
  loadCheck = false;
  isLoadingEmail = false;
  isDataEmail = true;
  isLoadingFax = false;
  isDataFax = true;
  statusBoth = 'both';
  statusSent = 'sent';
  statusEmail = 'email';
  statusFax = 'fax';
  statusNot = 'not';
  emailSendStatus = 'wait';
  faxSendStatus = 'wait';
  public emailsDropdown = new Observable<string[]>();
  public faxesDropdown = new Observable<string[]>();
  emailList = new Array<string>();
  faxList = new Array<string>();
  emailSuccess = ShareReport.emailSuccess;
  emailError = ShareReport.emailError;
  faxSuccess = ShareReport.faxSuccess;
  faxError = ShareReport.faxError;
  emailFaxFetchError = AcknowledgementDialog.Warning;
  ValidationMessages: any;

  constructor(public dialogRef: MatDialogRef<ShareReportDialogComponent>, private readonly fb: UntypedFormBuilder,
              @Inject(MAT_DIALOG_DATA) public data: { result: OrderResult, report: ReportDTO }, private resultService: ResultsService,
              private createService: CreateCustomerUserService,
              private notification: NotificationService,
              private loader: LoaderService) { }

  ngOnInit() {
    this.shareControl = this.fb.group({
      emails: ['', [Validators.maxLength(100), Validators.pattern(Constants.EMAIL_STANDARD_REGEX)]],
      faxes: ['', [Validators.minLength(10)]]
    });
    this.emailsFaxesInitialization();
    this.ValidationMessages = CreateUpdateProfileFormError.ReturnCreateEditUserErrors();
  }

  private emailsFaxesInitialization() {
    const accountList = this.data.result.authorizedAccountCodes;
    accountList.push(this.data.result.mainAccountCode);
    this.emailsFaxesServiceCall(accountList).then((res: EmailFaxShare) => {
      this.emailList = res.emails;
      this.faxList = res.faxNumbers;
      this.emailsDropdown = of(this.emailList);
      this.faxesDropdown = of(this.faxList);
      this.emailsDropdown = this.shareControl.controls.emails.valueChanges
        .pipe(
          startWith(''),
          map(value => value),
          map(name => name ? this._filterEmail(name) : this.emailList.slice())
        );
      this.faxesDropdown = this.shareControl.controls.faxes.valueChanges
        .pipe(
          startWith(''),
          map(value => value),
          map(name => name ? this._filterFax(name) : this.faxList.slice())
        );
    });
  }

  private async emailsFaxesServiceCall(accountList: string[]) {
    const promise = new Promise((resolve, reject) => {
      this.resultService.getEmailsFaxNumbers(accountList).subscribe({
        next: (res) => {
          resolve(res);
        },
        error: (error) => {
          reject(this.emailFaxFetchError);
      }
      });
    });
    return await promise;
  }

  public emailCall(event: MatAutocompleteSelectedEvent) {
    if (event.option) {
      this.shareControl.controls.emails.setValue(event.option.viewValue);
    }
  }
  public faxCall(event: MatAutocompleteSelectedEvent) {
    if (event.option) {
      this.shareControl.controls.faxes.setValue(event.option.viewValue);
    }
  }

  private _filterEmail(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.emailList.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
  }

  private _filterFax(value: string): string[] {
    const filterValue = value.toLowerCase();

    return this.faxList.filter(option => option.toLowerCase().indexOf(filterValue) === 0);
  }

  saveDisableCheck() {
    if (!this.shareControl.invalid && (
      this.shareControl.controls.emails.value !== ''
      || (this.shareControl.controls.faxes.value !== '' && this.shareControl.controls.faxes.value.length >= 10))) {
      return false;
    } else { return true; }
  }

  formatPhone() {
    if (this.shareControl.controls.faxes.value !== '') {
      this.createService.formatPhone(this.shareControl.controls.faxes);
    }
  }

  formatPhoneRemove() {
    if (this.shareControl.controls.faxes.value !== '' && this.shareControl.controls.faxes.value !== undefined) {
      this.shareControl.controls.faxes.setValue(this.shareControl.controls.faxes.value.split('-').join(''));
      this.shareControl.updateValueAndValidity();
    }
  }

  send() {
    this.loader.show();
    const request = new EmailFaxRequest();
    request.reportID = this.data.report.reportId;
    request.accessionId = this.data.result.accessionId;
    request.bu = this.data.result.buCode;
    if (!this.shareControl.invalid
      && this.shareControl.controls.emails.value !== ''
      && this.shareControl.controls.faxes.value !== '') {
      this.sendEmail(request, this.statusBoth);
    } else if (!this.shareControl.invalid
      && this.shareControl.controls.emails.value !== '') {
      this.sendEmail(request, this.statusEmail);
    } else if (!this.shareControl.invalid
      && this.shareControl.controls.faxes.value !== '') {
      this.sendFax(request, this.statusFax);
    }
  }

  async sendEmail(request: EmailFaxRequest, callType: string) {
    if (!this.shareControl.invalid && this.shareControl.controls.emails.value !== '') {
      request.email = this.shareControl.controls.emails.value;
      if (this.shareControl.controls.faxes.value !== '') {
        request.faxNumber = this.shareControl.controls.faxes.value.split('-').join('');
      }
      const res = await this.resultService.reportSend(request);
      if (res === true) {
          this.emailSendStatus = this.statusSent;
          this.finalClose(callType);
        } else {
          this.emailSendStatus = this.statusNot;
          this.finalClose(callType);
      }
      if (callType === this.statusBoth) {
        this.sendFax(request, this.statusBoth);
      }
      }
  }

  private handleBothSendStatus() {
    if (this.emailSendStatus === this.statusSent && this.faxSendStatus === this.statusSent) {
      this.CloseDialog();
      this.notification.success(this.emailSuccess);
      setTimeout(function() { this.notification.success(this.faxSuccess); }.bind(this), 2000);
    } else if (this.emailSendStatus === this.statusSent && this.faxSendStatus === this.statusNot) {
      this.resetMessageObject();
      this.notification.success(this.emailSuccess);
      setTimeout(function() { this.notification.warn(this.faxError); }.bind(this), 2000);
    } else if (this.emailSendStatus === this.statusNot && this.faxSendStatus === this.statusSent) {
      this.resetMessageObject();
      this.notification.success(this.faxSuccess);
      setTimeout(function() { this.notification.warn(this.emailError); }.bind(this), 2000);
    } else if (this.emailSendStatus === this.statusNot && this.faxSendStatus === this.statusNot) {
      this.resetMessageObject();
      this.notification.warn(this.emailError);
      setTimeout(function() { this.notification.warn(this.faxError); }.bind(this), 2000);
    }
  }

  finalClose(callType: string) {
    if (callType === this.statusBoth) {
      this.handleBothSendStatus();
    } else if (callType === this.statusEmail) {
      if (this.emailSendStatus === this.statusSent) {
        this.CloseDialog();
        this.notification.success(this.emailSuccess);
      } else if (this.emailSendStatus === this.statusNot) {
        this.resetMessageObject();
        this.notification.warn(this.emailError);
      }
    } else if (callType === this.statusFax) {
      if (this.faxSendStatus === this.statusSent) {
        this.CloseDialog();
        this.notification.success(this.faxSuccess);
      } else if (this.faxSendStatus === this.statusNot) {
        this.resetMessageObject();
        this.notification.warn(this.faxError);
      }
    }
    this.loader.hide();
  }

  async sendFax(request: EmailFaxRequest, callType: string) {
    if (!this.shareControl.invalid && this.shareControl.controls.faxes.value !== '') {
      request.faxNumber = this.shareControl.controls.faxes.value.split('-').join('');
      if (this.shareControl.controls.emails.value !== '') {
        request.email = this.shareControl.controls.emails.value;
      }
      const res = await this.resultService.reportSendFax(request);
      if (res) {
        if (res === true) {
          this.faxSendStatus = this.statusSent;
          this.finalClose(callType);
        } else {
          this.faxSendStatus = this.statusNot;
          this.finalClose(callType);
        }
      } else {
        this.faxSendStatus = this.statusNot;
        this.finalClose(callType);
      }
    }
  }

  resetMessageObject() {
    this.emailSendStatus = '';
    this.faxSendStatus = '';
  }

    CloseDialog() {
    this.dialogRef.close(false);
  }
}
